import type { ReactNode } from 'react';
import { Icon } from '@/shared/ui/Icon';
import { LpFooter, LpShell } from '@/shared/components/LpShell';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import type { FeatureIcon } from '@/modules/tenant/domain/entities/tenant.entity';
import { HeaderAccount } from '@/modules/auth/presentation/components/HeaderAccount';
import {
  SendGabaritoAction,
  useGabaritoSubmission,
} from '@/modules/gabarito/presentation/components/SendGabaritoAction';
import { Link } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { FaqSection } from '../components/FaqSection';

/**
 * A LP de uma prova. O layout é o da home do `enamed-feedback-lp` — hero,
 * vídeo, passos, argumentos, fechamento e FAQ, nessa ordem — e todo o texto
 * vem do tenant. **O funil segue o `gabaritoSubmission` da edição**:
 *
 * - `open`: a home é do gabarito — texto e botões (hero e fechamento) levam
 *   ao envio, e o ensaio sai da home;
 * - `soon`/`closed`: a home é do ensaio, com o gabarito como botão
 *   secundário (em breve) ou sem ele (encerrado).
 */
export function LandingPage() {
  const { exam, theme, content } = useTenant();
  const { hero, steps, features, closer } = content;
  const isGabaritoOpen = useGabaritoSubmission() === 'open';

  return (
    <LpShell theme={theme} headerRight={<HeaderAccount />}>
      {/* React 19 sobe `<title>` e `<meta>` para o `<head>`. */}
      <title>{content.seo.title}</title>
      <meta name="description" content={content.seo.description} />
      <meta name="theme-color" content={theme.ink} />

      <section className="hero">
        <div className="glow" aria-hidden="true" />
        <div className="wrap">
          <div className="hero-inner">
            <p className="kicker rise d1" title={exam.fullName}>
              {exam.shortName}
            </p>
            <h1 className="rise d2">{hero.title}</h1>
            <p className="lead rise d3">{hero.lead}</p>
            <div className="hero-actions rise d4">
              {isGabaritoOpen ? (
                <SendGabaritoAction
                  variant="cta"
                  local="hero"
                  label={hero.ctaLabel}
                />
              ) : (
                <>
                  <Link
                    className="cta cta-inline"
                    to="/ensaio"
                    onClick={() =>
                      track({
                        event: 'cta_clicado',
                        cta: 'comecar_ensaio',
                        local: 'hero',
                      })
                    }
                  >
                    {hero.ctaLabel}
                  </Link>
                  {/* Em breve (desabilitado) ou some, conforme o
                      `gabaritoSubmission` da edição. */}
                  <SendGabaritoAction
                    variant="ghost"
                    local="hero"
                    label={hero.secondaryLabel}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {content.youtubeVideoId && (
        <section className="fold fold-center">
          <div className="wrap">
            <h2 className="claim">Veja como funciona</h2>
            <div className="video-frame">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${content.youtubeVideoId}`}
                title={`Como funciona o ensaio da ${exam.shortName}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      <section className="fold fold-alt fold-center" id="como-funciona">
        <div className="wrap">
          <h2 className="claim">Como funciona</h2>
          <p className="claim-sub">{content.stepsSubtitle}</p>

          <ul className="pair trio">
            {steps.map((step, index) => (
              <li className="card" key={step.title}>
                <span className="step-num" aria-hidden="true">
                  {index + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="fold fold-center">
        <div className="wrap">
          <h2 className="claim">{content.featuresTitle}</h2>

          <ul className="pair trio">
            {features.map((feature) => (
              <li className="card" key={feature.title}>
                <span className="card-icon">{FEATURE_ICONS[feature.icon]}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* O fechamento vem antes do FAQ, como no ENAMED: quem já se convenceu
          não deve precisar rolar as perguntas para achar o botão. */}
      <section className="fold fold-alt fold-center">
        <div className="wrap">
          <div className="closer">
            <p>{closer.title}</p>
            <p className="closer-sub">{closer.subtitle}</p>
            {isGabaritoOpen ? (
              <SendGabaritoAction
                variant="cta"
                local="fechamento"
                label={hero.ctaLabel}
              />
            ) : (
              <Link
                className="cta"
                to="/ensaio"
                onClick={() =>
                  track({
                    event: 'cta_clicado',
                    cta: 'comecar_ensaio',
                    local: 'fechamento',
                  })
                }
              >
                {hero.ctaLabel}
              </Link>
            )}
          </div>
        </div>
      </section>

      <FaqSection groups={content.faq} />

      <LpFooter />
    </LpShell>
  );
}

const FEATURE_ICONS: Record<FeatureIcon, ReactNode> = {
  chart: (
    <Icon size="md">
      <rect x="4" y="12" width="4" height="8" rx="1" />
      <rect x="10" y="8" width="4" height="12" rx="1" />
      <rect x="16" y="4" width="4" height="16" rx="1" />
    </Icon>
  ),
  target: (
    <Icon size="md">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    </Icon>
  ),
  clock: (
    <Icon size="md">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </Icon>
  ),
};
